/**
 * Contract Intelligence's pure presentation rules: paths, labels, how a
 * review's progress reads, how gaps group and order, how flagged passages
 * split a document into highlighted runs, how reviewed documents group by
 * counterparty, and what "Ask Amoreg" asks. Unit-tested in `ciModel.test.ts`.
 *
 * @module components/apps/contractIntelligence/ciModel
 */
import type {
  CiBasis,
  CiDocument,
  CiDocumentListing,
  CiGap,
  CiReview,
  CiReviewStatus,
  CiReviewSummary,
  CiSeverity,
} from "../../../state/contractIntelligence";

export const CI_APP_ID = "contract-intelligence";

/** The area's sections, as path segments under `/$org/apps/contract-intelligence`. */
export const CI_SECTIONS = ["new", "reviews", "documents", "templates", "settings"] as const;
export type CiSection = (typeof CI_SECTIONS)[number] | "overview" | "review";

/** Which section a pathname is in (the sidebar's active row). */
export function ciSectionFromPathname(pathname: string): CiSection | null {
  const segments = pathname.split("/").filter(Boolean);
  if (segments[1] !== "apps" || segments[2] !== CI_APP_ID) return null;
  const section = segments[3];
  if (section === undefined) return "overview";
  if (section === "reviews" && segments[4]) return "review";
  return (CI_SECTIONS as ReadonlyArray<string>).includes(section) ? (section as CiSection) : null;
}

/** The review a pathname shows, or null. */
export function ciReviewIdFromPathname(pathname: string): string | null {
  const segments = pathname.split("/").filter(Boolean);
  if (!(segments[2] === CI_APP_ID && segments[3] === "reviews" && segments[4])) return null;
  try {
    return decodeURIComponent(segments[4]);
  } catch {
    // A malformed escape (`%E0`) names no review; never throw from a render.
    return null;
  }
}

export const SEVERITY_ORDER: ReadonlyArray<CiSeverity> = ["critical", "high", "medium", "low"];

export const SEVERITY_LABEL: Readonly<Record<CiSeverity, string>> = {
  critical: "Critical",
  high: "High",
  medium: "Medium",
  low: "Low",
};

export const STATUS_LABEL: Readonly<Record<CiReviewStatus, string>> = {
  draft: "Not run",
  queued: "Starting",
  running: "Reviewing",
  completed: "Complete",
  failed: "Failed",
};

export const BASIS_KIND_LABEL: Readonly<Record<CiBasis["kind"], string>> = {
  policies: "Policies",
  regime: "Regime",
  template: "Template",
};

export const isActive = (status: CiReviewStatus): boolean => status === "queued" || status === "running";

/** A document name as people read it: a file name's underscores become spaces. */
export const readableName = (name: string): string => name.replace(/_+/g, " ").replace(/\s+/g, " ").trim();

/** "Fees Policy", "Fees Policy and 2 more", "AIFMD" — what a review is checked against, in a line. */
export function basisLine(basis: CiBasis): string {
  const [first, ...rest] = basis.items;
  if (!first) return BASIS_KIND_LABEL[basis.kind];
  return rest.length > 0 ? `${readableName(first.name)} and ${rest.length} more` : readableName(first.name);
}

export function openGapCount(summary: Pick<CiReviewSummary, "gaps">): number {
  const open = summary.gaps.open;
  return open.critical + open.high + open.medium + open.low;
}

/** The most severe open gap's severity, or null when nothing is open. */
export function highestOpenSeverity(summary: Pick<CiReviewSummary, "gaps">): CiSeverity | null {
  return SEVERITY_ORDER.find((severity) => summary.gaps.open[severity] > 0) ?? null;
}

export interface ReviewProgress {
  /** 0–1: how much of the documents the run has read. */
  readonly fraction: number;
  /** What the run is doing, in a few words. */
  readonly phase: string;
}

/** How far an in-flight run has got, from what it has read and reported. */
export function reviewProgress(
  review: Pick<CiReview, "status" | "progress">,
  documents: ReadonlyArray<Pick<CiDocument, "id" | "name" | "charCount">>,
  gapCount: number,
): ReviewProgress {
  const total = documents.reduce((sum, document) => sum + document.charCount, 0);
  const read = documents.reduce(
    (sum, document) => sum + Math.min(document.charCount, review.progress.read[document.id] ?? 0),
    0,
  );
  const fraction = total > 0 ? Math.min(1, read / total) : 0;
  if (review.status === "queued") return { fraction: 0, phase: "Starting the review" };
  if (review.status !== "running") return { fraction: review.status === "completed" ? 1 : fraction, phase: STATUS_LABEL[review.status] };
  const reading = documents.find((document) => (review.progress.read[document.id] ?? 0) < document.charCount);
  if (read === 0) return { fraction, phase: "Reading what to check against" };
  if (reading) return { fraction, phase: `Reading ${reading.name}` };
  return { fraction, phase: gapCount > 0 ? "Checking clauses and reporting gaps" : "Checking clauses" };
}

export type GapGrouping = "severity" | "rule" | "document";

export interface GapGroup {
  readonly key: string;
  readonly label: string;
  readonly severity: CiSeverity | null;
  readonly gaps: ReadonlyArray<CiGap>;
}

const severityRank = (severity: CiSeverity) => SEVERITY_ORDER.indexOf(severity);

/** Open gaps first, then by severity, then by where they sit in the document. */
export function compareGaps(a: CiGap, b: CiGap): number {
  const decided = (gap: CiGap) => (gap.status === "open" ? 0 : 1);
  return (
    decided(a) - decided(b) ||
    severityRank(a.severity) - severityRank(b.severity) ||
    a.documentId.localeCompare(b.documentId) ||
    a.startOffset - b.startOffset
  );
}

/** Gaps grouped for the results panel; empty groups are left out. */
export function groupGaps(
  gaps: ReadonlyArray<CiGap>,
  grouping: GapGrouping,
  documents: ReadonlyArray<Pick<CiDocument, "id" | "name">> = [],
): ReadonlyArray<GapGroup> {
  const sorted = [...gaps].sort(compareGaps);
  if (grouping === "severity") {
    return SEVERITY_ORDER.map((severity) => ({
      key: severity,
      label: SEVERITY_LABEL[severity],
      severity,
      gaps: sorted.filter((gap) => gap.severity === severity),
    })).filter((group) => group.gaps.length > 0);
  }
  const groups = new Map<string, { label: string; gaps: Array<CiGap> }>();
  for (const gap of sorted) {
    const key = grouping === "rule" ? `${gap.rule.reference}\u0000${gap.rule.label}` : gap.documentId;
    const label =
      grouping === "rule"
        ? gap.rule.label
        : (documents.find((document) => document.id === gap.documentId)?.name ?? "Document");
    const entry = groups.get(key) ?? { label, gaps: [] };
    entry.gaps.push(gap);
    groups.set(key, entry);
  }
  return [...groups.entries()].map(([key, entry]) => ({
    key,
    label: entry.label,
    severity: entry.gaps.map((gap) => gap.severity).sort((a, b) => severityRank(a) - severityRank(b))[0] ?? null,
    gaps: entry.gaps,
  }));
}

export interface TextRun {
  readonly start: number;
  readonly end: number;
  readonly text: string;
  /** Gaps whose passage covers this run, most severe first; empty for plain text. */
  readonly gapIds: ReadonlyArray<string>;
  readonly severity: CiSeverity | null;
}

/**
 * The document split into runs at every passage boundary, so overlapping
 * passages highlight correctly: each run knows every gap covering it.
 * Ranges outside the text are clamped; empty ranges are ignored.
 */
export function highlightRuns(text: string, gaps: ReadonlyArray<Pick<CiGap, "id" | "startOffset" | "endOffset" | "severity">>): ReadonlyArray<TextRun> {
  const ranges = gaps
    .map((gap) => ({
      id: gap.id,
      severity: gap.severity,
      start: Math.max(0, Math.min(text.length, gap.startOffset)),
      end: Math.max(0, Math.min(text.length, gap.endOffset)),
    }))
    .filter((range) => range.end > range.start);
  const cuts = new Set<number>([0, text.length]);
  for (const range of ranges) {
    cuts.add(range.start);
    cuts.add(range.end);
  }
  const points = [...cuts].sort((a, b) => a - b);
  const runs: Array<TextRun> = [];
  for (let index = 0; index < points.length - 1; index += 1) {
    const start = points[index]!;
    const end = points[index + 1]!;
    if (end <= start) continue;
    const covering = ranges
      .filter((range) => range.start <= start && range.end >= end)
      .sort((a, b) => severityRank(a.severity) - severityRank(b.severity));
    runs.push({
      start,
      end,
      text: text.slice(start, end),
      gapIds: covering.map((range) => range.id),
      severity: covering[0]?.severity ?? null,
    });
  }
  return runs;
}

export interface CounterpartyGroup {
  readonly key: string;
  readonly counterparty: string | null;
  readonly documents: ReadonlyArray<CiDocumentListing>;
  readonly openGaps: number;
}

/** Reviewed documents by counterparty (named ones alphabetically, then "No counterparty"). */
export function groupByCounterparty(documents: ReadonlyArray<CiDocumentListing>, query = ""): ReadonlyArray<CounterpartyGroup> {
  const needle = query.trim().toLowerCase();
  const matches = needle
    ? documents.filter((document) =>
        [document.name, document.counterparty ?? "", document.documentType ?? "", document.reviewTitle]
          .join(" ")
          .toLowerCase()
          .includes(needle),
      )
    : documents;
  const groups = new Map<string, { counterparty: string | null; documents: Array<CiDocumentListing> }>();
  for (const document of matches) {
    const name = document.counterparty?.trim() || null;
    const key = name ? name.toLowerCase() : "\u0000none";
    const entry = groups.get(key) ?? { counterparty: name, documents: [] };
    entry.documents.push(document);
    groups.set(key, entry);
  }
  return [...groups.entries()]
    .map(([key, entry]) => ({
      key,
      counterparty: entry.counterparty,
      documents: entry.documents,
      openGaps: entry.documents.reduce((sum, document) => sum + document.openGaps, 0),
    }))
    .sort((a, b) =>
      a.counterparty === null ? 1 : b.counterparty === null ? -1 : a.counterparty.localeCompare(b.counterparty),
    );
}

/** The question "Ask Amoreg" opens a chat with: the gap, its passage and the rule, ready to discuss. */
export function askAmoregPrompt(input: {
  readonly reviewTitle: string;
  readonly documentName: string;
  readonly gap: Pick<CiGap, "title" | "passage" | "explanation" | "recommendation" | "rule" | "severity">;
}): string {
  const { gap } = input;
  const lines = [
    `In the Contract Intelligence review "${input.reviewTitle}", ${input.documentName} was flagged (${SEVERITY_LABEL[gap.severity].toLowerCase()}): ${gap.title}.`,
    "",
    `Passage: "${gap.passage.length > 900 ? `${gap.passage.slice(0, 900)}…` : gap.passage}"`,
    `Measured against: ${gap.rule.label}${gap.rule.quote ? ` — "${gap.rule.quote}"` : ""}`,
    `Why it falls short: ${gap.explanation}`,
  ];
  if (gap.recommendation) lines.push(`Suggested fix: ${gap.recommendation}`);
  lines.push("", "Is this a real gap for us, and what wording would close it?");
  return lines.join("\n");
}

/** "12 Oct", or "12 Oct 2025" outside the current year. */
export function shortDate(iso: string, now = new Date()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
  });
}

/** "4 min", "2 h", "3 d" since an instant. */
export function ageLabel(iso: string, now = Date.now()): string {
  const ms = now - Date.parse(iso);
  if (!Number.isFinite(ms) || ms < 60_000) return "now";
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} h`;
  return `${Math.floor(hours / 24)} d`;
}

export function fileSizeLabel(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * The form draft after the stored value refreshes. The page's data refreshes
 * on every write anywhere in the organization, so a draft the person has
 * edited (it no longer equals what was last loaded) is kept; an untouched one
 * follows the new stored value.
 */
export function refreshedDraft<A>(draft: A | null, lastLoaded: A | null, loaded: A): A {
  if (draft === null || lastLoaded === null) return loaded;
  return JSON.stringify(draft) === JSON.stringify(lastLoaded) ? loaded : draft;
}
